import { useCallback } from 'react';

import type { Currency } from '#src/__generated__/globalTypes.ts';
import type { Props as MonetaryInputProps } from '#src/atoms/inputs/MonetaryInput/index.tsx';
import { MonetaryInput } from '#src/atoms/inputs/MonetaryInput/index.tsx';
import type {
  Props as FieldProps,
  RenderProps,
} from '#src/components/form/Form/Field/index.tsx';
import { Field } from '#src/components/form/Form/Field/index.tsx';
import { useCurrentUserContext } from '#src/contexts/currentUser/index.ts';
import { useCurrentUserCurrencies } from '#src/hooks/useCurrentUserCurrencies.tsx';
import type { MonetaryAmount } from '#src/lib/monetaryAmount.ts';

type Props = {
  name: string;
  defaultCurrency?: Currency;
  onChange?: (monetaryAmount: MonetaryAmount) => void;
  onToggleEthDisplay?: (ethDisplay: boolean) => void;
  placeholder?: string;
} & Omit<FieldProps<MonetaryAmount>, 'render'> &
  Omit<
    MonetaryInputProps,
    'fiatCurrency' | 'onChange' | 'onlyShowFiatCurrency' | 'cryptocurrency'
  >;

export const MonetaryInputField = ({
  name,
  defaultValue,
  onChange,
  validate,
  ...rest
}: Props) => {
  const {
    walletPreferences: { onlyShowFiatCurrency },
  } = useCurrentUserContext();
  const { fiatCurrency, cryptocurrency } = useCurrentUserCurrencies();

  const render = useCallback(
    ({ error, handleChange, disabled }: RenderProps<MonetaryAmount>) => {
      return (
        <MonetaryInput
          defaultValue={defaultValue}
          fiatCurrency={fiatCurrency}
          cryptocurrency={cryptocurrency}
          onChange={(newMonetaryAmount, target) => {
            onChange?.(newMonetaryAmount);
            handleChange(newMonetaryAmount, target);
          }}
          onlyShowFiatCurrency={onlyShowFiatCurrency}
          error={error}
          {...rest}
          disabled={disabled || rest.disabled}
        />
      );
    },
    [
      defaultValue,
      fiatCurrency,
      cryptocurrency,
      onlyShowFiatCurrency,
      rest,
      onChange,
    ]
  );

  return (
    <Field
      defaultValue={defaultValue}
      name={name}
      validate={validate}
      render={render}
    />
  );
};
